import { useEffect, useRef, useState } from 'react';

import {
  AsyncStateFunction,
  UseUpdatingAsyncStateProps,
  UseUpdatingAsyncStateResponse,
} from '../../types/web3/useUpdatableAsyncState';
import { captureError } from '../../utils/capture';

// merges loading/error values of multiple states
export const mergeUpdatableAsyncStates = <T = any, E = Error>(
  states: UseUpdatingAsyncStateResponse<T, E>[],
) => {
  const stateWithError = states.find((s) => s.error);
  const stateWithLoading = states.find((s) => s.loading);
  const didAllSettled = states.every((s) => s.settled);
  return {
    settled: didAllSettled,
    error: stateWithError ? stateWithError.error : undefined,
    loading: stateWithLoading ? stateWithLoading.loading : false,
    reset: () => states.forEach((s) => s.reset()),
  };
};

/**
 * Makes async operations simpler while implementing loading/error and updating the value using async operations.
 */
export const useUpdatableAsyncState = <
  T extends unknown = any,
  E extends unknown = Error,
  O extends unknown = undefined,
>({
  cleanup,
  defaultValue,
  deps = [],
  fn,
  requirements = true,
}: UseUpdatingAsyncStateProps<T>) => {
  const [error, setError] = useState<E>();
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<T>(defaultValue);
  const [settled, setSettled] = useState<boolean>(false);
  const valueRef = useRef<T>(defaultValue);
  valueRef.current = value;

  const updateState = async (func: AsyncStateFunction<T>) => {
    setLoading(true);
    try {
      const val = await func(valueRef.current);
      setValue(val);
      setError(undefined);
    } catch (e) {
      captureError(e);
      setError(e);
    }
    setLoading(false);
    setSettled(true);
  };

  useEffect(() => {
    !loading && requirements && updateState(fn);
    return () => {
      cleanup && cleanup(valueRef.current);
    };
  }, deps);

  return {
    error,
    loading,
    value,
    updateState,
    settled,
    retry: () => updateState(fn),
    reset: () => {
      setError(undefined);
      setLoading(false);
      setValue(defaultValue);
      setSettled(false);
    },
  } as UseUpdatingAsyncStateResponse<T, E, O>;
};
